import {LocalStorageUtils} from '../../infrastructure/utils/local-storage.utils';
import {LOCAL_STORAGE_KEYS} from "../consts";
import {BaseManager} from "../../infrastructure/managers/base-manager";
import {LoggerUtils} from "../../infrastructure/utils/logger/logger-utils";
import {SiteConfig} from "../bootstrap/models/site-config";
import {DeviceStore} from "../app/device.store";
import {singleton} from "../../infrastructure/decorators/singleton";
import {PopupDisplayRemoteConfigModel} from "../models/popup/popup-display.remote-config.model";
import {DomUtils} from "../../infrastructure/utils/dom-utils";
import {action, autorun, makeObservable, observable} from "mobx";

@singleton
export class PopupsDisplayManager extends BaseManager {
  popupDisplayCounterDictionary: Map<Object>;
  pageNum: Number;
  popups: Array<PopupDisplayRemoteConfigModel>;
  @observable callbacks = {}
  validPopupModels = []

  static injectables = [...BaseManager.injectables, LocalStorageUtils];

  constructor(
    domUtils: DomUtils,
    logger: LoggerUtils,
    siteConfig: SiteConfig,
    deviceStore: DeviceStore,
    localStorageUtils: LocalStorageUtils) {
    super(domUtils, logger, siteConfig, deviceStore);

    this.localStorageUtils = localStorageUtils;
    this.displayDictionaryKey = LOCAL_STORAGE_KEYS.popupAutoDisplayCounters;
    this.pageNum = 0;

    makeObservable(this)
  }

  init(remoteConfigModels: Object) {
    this.popupDisplayCounterDictionary = this._getDisplayDictionary();

    if (this.popupDisplayCounterDictionary === undefined) {
      this.popupDisplayCounterDictionary = {};
      this._setDisplayDictionary()
    }

    let sessionCounter: Number = this.localStorageUtils.get(LOCAL_STORAGE_KEYS.popupSessionCounter) || 0;
    this.localStorageUtils.set(LOCAL_STORAGE_KEYS.popupSessionCounter, ++sessionCounter)

    this.popups = remoteConfigModels?.map(popup => new PopupDisplayRemoteConfigModel(popup, this.popupDisplayCounterDictionary, this.localStorageUtils));

    this.handlePopupDisplayPriority();

    autorun(() => {
      if (Object.keys(this.callbacks).length === this.popups?.length) {
        this.calcVisiblePopup();
      }
    })
  }

  _setDisplayDictionary() {
    this.localStorageUtils.set(this.displayDictionaryKey, this.popupDisplayCounterDictionary);
  }

  _getDisplayDictionary() {
    return this.localStorageUtils.get(this.displayDictionaryKey)
  }

  handleRouteChange() {
    this.pageNum++;

    if (this.pageNum > 1) {
      this.calcVisiblePopup();
    }
  }

  calcVisiblePopup() {
    if (this.popups) {
      this.validPopupModels = [];
      for (const item of this.popups.sort((a, b) => a.prioritization - b.prioritization || b.nextDisplaySession - a.nextDisplaySession)) {
        const isVisible = this.shouldDisplayPopup(item);

        if (isVisible) {
          const params = {
            display: true,
            delay: item.delay,
            id: item.name
          }
          this.validPopupModels.push(params)
        }
      }
      if (this.validPopupModels.length > 0) {
        this.activateNextPopupToDisplay()
      }

    }
  }

  get currentSession(): Number {
    return this.localStorageUtils.get(LOCAL_STORAGE_KEYS.popupSessionCounter) || 1;
  }

  disablePopup(popupId: string): boolean {
    this.updateNextDisplaySession(popupId, -1);
  }

  @action registerToChange(id, callback) {
    this.callbacks[id] = callback;
  }

  removeCallback(id) {
    delete this.callbacks[id];
  }

  activateNextPopupToDisplay() {
    if (this.validPopupModels.length > 0) {
      const popUpToDisplay = this.validPopupModels.shift();

      if (this.callbacks[popUpToDisplay.id]) {
        this.callbacks[popUpToDisplay.id]({display: popUpToDisplay.display, delay: popUpToDisplay.delay});
      }
    }

    return true
  }

  shouldDisplayPopup(popupModel: PopupDisplayRemoteConfigModel): Boolean {
    let shouldDisplayPopup: Boolean = false;
    const isPopupModelCallbackExist: Boolean = !!this.callbacks[popupModel.name];

    if (isPopupModelCallbackExist && this.popupDisplayCounterDictionary) {
      const isPopUpFirstDisplay = this.popupDisplayCounterDictionary[popupModel.name].visibleCounter === 0;
      const nextDisplaySession = this.popupDisplayCounterDictionary[popupModel.name].nextDisplaySession;

      if (this.maxNumberOfDisplayValid(popupModel) && nextDisplaySession !== -1) {
        if (nextDisplaySession <= this.currentSession && ((isPopUpFirstDisplay && this.pageNum >= popupModel.displayPageInFirstSession) || !isPopUpFirstDisplay)) {
          shouldDisplayPopup = true;
        }
      }
    }

    return shouldDisplayPopup;
  }

  setPopupDisplayStatus(id, isVisible) {
    return isVisible ? this.handleVisiblePopup(id) : this.activateNextPopupToDisplay()
  }

  handleVisiblePopup(popupId: String) {
    const popupModel = this.popups?.find((popup) => popupId === popup.name);
    //update localStorage visible counter of popup
    popupModel.visibleCounter = this.updateVisibleCounter(popupId);
    // update next display session of popup
    popupModel.nextDisplaySession = this.calcNextDisplaySession(popupModel);
    // reorder popups
    this.handlePopupDisplayPriority();

    return popupModel.visibleCounter;
  }

  calcNextDisplaySession(popupModel: PopupDisplayRemoteConfigModel) {
    const currentSession = this.currentSession;
    let nextDisplaySession = popupModel.firstSessionToDisplay;
    if (popupModel.visibleCounter > 0) {
      if (popupModel.visibleCounter >= popupModel.maximumDisplays) {
        nextDisplaySession = -1;
      } else {
        nextDisplaySession = currentSession + popupModel.nextDisplayVisits;
      }
    }

    return nextDisplaySession;
  }

  handlePopupDisplayPriority() {
    let hasDuplicatesNextSessionDisplay = true;

    while (hasDuplicatesNextSessionDisplay) {
      // Create the dictionary from the current state of popups
      const popupDictionary = this.createPopupDictionary(this.popups, "nextDisplaySession");

      hasDuplicatesNextSessionDisplay = false;

      for (const [key, items] of Object.entries(popupDictionary)) {
        if (items.length > 1 && Number(key) !== -1) {
          items.sort((a, b) => a.prioritization - b.prioritization);

          const itemToDisplay = items.shift();

          for (const item of items) {
            // If item has the same display page as the highest priority item, update session
            if (itemToDisplay.pageToDisplay === item.pageToDisplay) {
              item.nextDisplaySession = Number(key) + 1;
              hasDuplicatesNextSessionDisplay = true; // Indicate that duplicates might still exist
            }
          }
        }
      }
    }

    for (const item of this.popups) {
      this.popupDisplayCounterDictionary[item.name].nextDisplaySession = item.nextDisplaySession;
    }

    this._setDisplayDictionary();
  }

  createPopupDictionary = (array, propertyName) => {
    return array.reduce((acc, item) => {
      const key = item[propertyName];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);

      return acc;
    }, {});
  };

  maxNumberOfDisplayValid(popupModel: PopupDisplayRemoteConfigModel): Boolean {
    let maxNumberOfDisplayValid;
    const popupMaxDisplays = popupModel?.maximumDisplays;
    const maxNumberOfDisplayStorageCount: String = this.popupDisplayCounterDictionary[popupModel.name].visibleCounter;

    if (!maxNumberOfDisplayStorageCount) {
      maxNumberOfDisplayValid = true;
    } else {
      const maxNumberOfDisplayCount: Number = Number(maxNumberOfDisplayStorageCount);
      maxNumberOfDisplayValid = maxNumberOfDisplayCount < popupMaxDisplays;
    }

    return maxNumberOfDisplayValid;
  }

  updateVisibleCounter(popupId: String): Boolean {
    if (this.popupDisplayCounterDictionary[popupId] === undefined && this.popupDisplayCounterDictionary[popupId].visibleCounter === undefined) {
      this.popupDisplayCounterDictionary[popupId].visibleCounter = 0;
    } else {
      this.popupDisplayCounterDictionary[popupId].visibleCounter++;
    }
    this._setDisplayDictionary();

    return this.popupDisplayCounterDictionary[popupId].visibleCounter;
  }

  updateNextDisplaySession(popupId: String, value): Boolean {
    if (this.popupDisplayCounterDictionary[popupId] === undefined || this.popupDisplayCounterDictionary[popupId].nextDisplaySession === undefined) {
      this.popupDisplayCounterDictionary[popupId].nextDisplaySession = -1;
    } else {
      this.popupDisplayCounterDictionary[popupId].nextDisplaySession = value;
    }

    this._setDisplayDictionary();
  }
}
